import axios from "axios"

// FORCE DEVELOPMENT MODE FOR DEMO
const FORCE_DEV_MODE = false;

export async function axiosQuery(url, config, callback, errorStore, errorTitleName) {
  // In production mode, make the actual API call
  try {
    return axios.get(url, config)
      .then(result => {
        if (callback && typeof callback === 'function') {
          callback(result?.data)
        }
        return result?.data
      })
      .catch(error => {
        if (errorStore) {
          errorManagement(error, errorStore, errorTitleName)
        }
        throw error // Re-throw so Promise.reject can catch it
      })
  } catch (error) {
    if (errorStore) {
      errorStore.setError("General Error " + errorTitleName, error.message)
    }
    throw error
  }
}

export async function axiosPostQuery(url, data, config, callback, errorStore, errorTitleName) {
  try {
    console.log('Making API POST call to:', url);
    const result = await axios.post(url, data, config);
    console.log('API response:', result?.data);
    if (callback && typeof callback === 'function') {
      callback(result?.data);
    }
    return result?.data;
  } catch (error) {
    console.error('API error:', error);
    if (errorStore) {
      errorManagement(error, errorStore, errorTitleName);
    }
    throw error; // Re-throw the error so it can be caught by the caller
  }
}

// Function to generate mock data based on the URL
function generateMockData(url, postData = null) {
  console.log('Generating enhanced mock data for URL:', url);
  
  // Extract the endpoint from the URL
  const endpoint = url.split('/').pop().split('?')[0];
  
  // Special case for portfolio/project-summary - needs specific budget breakdown
  if (url.includes('portfolio/project-summary')) {
    // Extract project ID if present
    const projectId = url.split('/').pop();
    
    // Create a realistic budget summary with specific categories
    const totalBudget = 25000000 + (Math.random() * 10000000);
    
    // Budget breakdown by major categories
    const budgetBreakdown = {
      acquisition: {
        amount: totalBudget * 0.22,
        percent: 22,
        label: "Acquisition"
      },
      hard: {
        amount: totalBudget * 0.55,
        percent: 55,
        label: "Hard"
      },
      soft: {
        amount: totalBudget * 0.15,
        percent: 15,
        label: "Soft"
      },
      finance: {
        amount: totalBudget * 0.05,
        percent: 5,
        label: "Misc/Finance"
      },
      contingencies: {
        amount: totalBudget * 0.03,
        percent: 3,
        label: "Contingencies"
      }
    };
    
    // Project progress
    const progressPercent = Math.floor(Math.random() * 100);
    const spentAmount = totalBudget * (progressPercent / 100);
    const remainingAmount = totalBudget - spentAmount;
    
    // Hard costs categories
    const hardCostCategories = [
      { name: "Site Work", amount: budgetBreakdown.hard.amount * 0.08, percent: 8 },
      { name: "Foundation", amount: budgetBreakdown.hard.amount * 0.12, percent: 12 },
      { name: "Structure", amount: budgetBreakdown.hard.amount * 0.20, percent: 20 },
      { name: "Exterior Enclosure", amount: budgetBreakdown.hard.amount * 0.15, percent: 15 },
      { name: "Roofing", amount: budgetBreakdown.hard.amount * 0.05, percent: 5 },
      { name: "Interior Construction", amount: budgetBreakdown.hard.amount * 0.15, percent: 15 },
      { name: "MEP Systems", amount: budgetBreakdown.hard.amount * 0.20, percent: 20 },
      { name: "Finishes", amount: budgetBreakdown.hard.amount * 0.05, percent: 5 }
    ];
    
    // Soft costs categories
    const softCostCategories = [
      { name: "Architecture & Engineering", amount: budgetBreakdown.soft.amount * 0.30, percent: 30 },
      { name: "Permits & Fees", amount: budgetBreakdown.soft.amount * 0.15, percent: 15 },
      { name: "Legal & Accounting", amount: budgetBreakdown.soft.amount * 0.10, percent: 10 },
      { name: "Insurance", amount: budgetBreakdown.soft.amount * 0.08, percent: 8 },
      { name: "Financing", amount: budgetBreakdown.soft.amount * 0.25, percent: 25 },
      { name: "Project Management", amount: budgetBreakdown.soft.amount * 0.07, percent: 7 },
      { name: "Testing & Inspection", amount: budgetBreakdown.soft.amount * 0.05, percent: 5 }
    ];
    
    // Schedule data
    const startDate = new Date(2022, 0, 15);
    const endDate = new Date(2023, 11, 31);
    const currentDate = new Date();
    const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysPassed = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
    const scheduleProgress = Math.min(100, Math.max(0, Math.floor((daysPassed / totalDays) * 100)));
    
    // Building use breakdown
    const buildingUse = [
      { 
        name: "Office", 
        gsf: 125000, 
        units: 0,
        percent: 60
      },
      { 
        name: "Retail", 
        gsf: 45000, 
        units: 12,
        percent: 20
      },
      { 
        name: "Residential", 
        gsf: 35000, 
        units: 28,
        percent: 20
      }
    ];
    
    // Total GSF
    const totalGsf = buildingUse.reduce((sum, use) => sum + use.gsf, 0);
    const totalUnits = buildingUse.reduce((sum, use) => sum + use.units, 0);
    
    // Building information
    const buildingInfo = {
      type: "Mixed-Use",
      stories: 12,
      total_gsf: totalGsf,
      total_units: totalUnits,
      address: "123 Main Street, New York, NY 10001",
      year_built: 2023,
      construction_type: "Type 1-A",
      occupancy_group: "B/M/R-2",
      zoning: "C6-4",
      sustainability: "LEED Gold",
      description: "A modern mixed-use development featuring office space, ground-floor retail, and luxury residential units."
    };
    
    // Timeline milestones
    const milestones = [
      { name: "Design Start", date: "2021-10-15", status: "Completed" },
      { name: "Design Complete", date: "2022-01-15", status: "Completed" },
      { name: "Permits Approved", date: "2022-03-01", status: "Completed" },
      { name: "Construction Start", date: "2022-03-15", status: "Completed" },
      { name: "Foundation Complete", date: "2022-06-30", status: "Completed" },
      { name: "Structure Complete", date: "2022-11-15", status: "Completed" },
      { name: "Building Envelope", date: "2023-02-28", status: "Completed" },
      { name: "MEP Rough-In", date: "2023-05-15", status: "In Progress" },
      { name: "Interior Finishes", date: "2023-09-30", status: "Planned" },
      { name: "Substantial Completion", date: "2023-11-15", status: "Planned" },
      { name: "Final Completion", date: "2023-12-31", status: "Planned" }
    ];
    
    // Return detailed project summary data
    return {
      project: {
        id: projectId || 6,
        name: `Project ${projectId || 6}`,
        status: "In Progress",
        building: buildingInfo,
        budget: {
          total: totalBudget,
          spent: spentAmount,
          remaining: remainingAmount,
          progress_percent: progressPercent,
          summary: [
            {
              label: budgetBreakdown.acquisition.label,
              percentage: budgetBreakdown.acquisition.percent,
              amount: budgetBreakdown.acquisition.amount
            },
            {
              label: budgetBreakdown.hard.label,
              percentage: budgetBreakdown.hard.percent,
              amount: budgetBreakdown.hard.amount
            },
            {
              label: budgetBreakdown.soft.label,
              percentage: budgetBreakdown.soft.percent,
              amount: budgetBreakdown.soft.amount
            },
            {
              label: budgetBreakdown.finance.label,
              percentage: budgetBreakdown.finance.percent,
              amount: budgetBreakdown.finance.amount
            },
            {
              label: budgetBreakdown.contingencies.label,
              percentage: budgetBreakdown.contingencies.percent,
              amount: budgetBreakdown.contingencies.amount
            }
          ],
          hard_costs: hardCostCategories,
          soft_costs: softCostCategories
        },
        schedule: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          progress_percent: scheduleProgress,
          milestones: milestones
        },
        building_use: buildingUse,
        team: {
          architect: "ABC Architects",
          engineer: "XYZ Engineers",
          general_contractor: "Best Builders Inc.",
          project_manager: "Jane Smith",
          owner_rep: "John Doe"
        }
      }
    };
  }
  
  // Enhanced mock data for common project details
  if (url.includes('/projects')) {
    // Mock projects data with more detailed information - ENSURE THIS IS AN ARRAY for projects.map to work
    return Array(10).fill().map((_, index) => ({
      id: index + 1,
      name: `Project ${index + 1}`,
      description: `Detailed description for Project ${index + 1}. This is a sample project with comprehensive data for demonstration purposes.`,
      address_line_1: `${100 + index} Main St`,
      city: 'New York',
      state: 'NY',
      zip_code: '10001',
      latitude: 40.7128 + (Math.random() * 0.1 - 0.05),
      longitude: -74.0060 + (Math.random() * 0.1 - 0.05),
      project_construction_types_s: ['Type A', 'Type B'],
      total_gsf: Math.floor(Math.random() * 1000000) + 50000,
      total_num_units: Math.floor(Math.random() * 500) + 10,
      planned_start_date: '2023-01-01',
      planned_end_date: '2024-01-01',
      actual_start_date: '2023-02-01',
      actual_end_date: '2024-03-01',
      asset_class: ['Residential', 'Commercial', 'Industrial', 'Hospitality', 'Retail', 'Civic & Cultural'][Math.floor(Math.random() * 6)],
      status: ['Planning', 'In Progress', 'Completed', 'On Hold'][Math.floor(Math.random() * 4)],
      construction_type: ['New Construction', 'Renovation', 'Addition', 'Mixed'][Math.floor(Math.random() * 4)],
      finish_level: ['Luxury', 'Standard', 'Economy', 'Custom'][Math.floor(Math.random() * 4)],
      budget: {
        total: Math.floor(Math.random() * 50000000) + 5000000,
        spent: Math.floor(Math.random() * 40000000) + 3000000,
        remaining: Math.floor(Math.random() * 10000000) + 1000000,
        variance: Math.floor(Math.random() * 2000000) - 1000000
      },
      timeline: {
        progress: Math.floor(Math.random() * 100),
        delay_days: Math.floor(Math.random() * 60) - 30,
      },
      team: {
        architect: `Architecture Firm ${index + 1}`,
        general_contractor: `Construction Co. ${index + 1}`,
        project_manager: `Manager ${index + 1}`
      }
    }));
  } else if (url.includes('fetch_total_costs_per_project')) {
    // Enhanced mock cost data for projects with more detail
    const mockCostData = {};
    for (let i = 1; i <= 10; i++) {
      mockCostData[i] = {
        total_revised: (Math.random() * 10000000 + 5000000).toFixed(2),
        total_original: (Math.random() * 9000000 + 4000000).toFixed(2),
        hard_costs: {
          original: (Math.random() * 7000000 + 3000000).toFixed(2),
          revised: (Math.random() * 7500000 + 3200000).toFixed(2),
          spent: (Math.random() * 6000000 + 2000000).toFixed(2)
        },
        soft_costs: {
          original: (Math.random() * 2000000 + 1000000).toFixed(2),
          revised: (Math.random() * 2500000 + 1200000).toFixed(2),
          spent: (Math.random() * 2000000 + 800000).toFixed(2)
        },
        variance: (Math.random() * 1000000 - 500000).toFixed(2)
      };
    }
    return mockCostData;
  } else if (url.includes('budget_variance')) {
    // Enhanced budget variance data with more quarters
    return {
      labels: ['Q1 2022', 'Q2 2022', 'Q3 2022', 'Q4 2022', 'Q1 2023', 'Q2 2023'],
      positive_variance: [
        Math.random() * 100, 
        Math.random() * 100, 
        Math.random() * 120, 
        Math.random() * 150,
        Math.random() * 180,
        Math.random() * 200
      ],
      negative_variance: [
        -Math.random() * 100, 
        -Math.random() * 100, 
        -Math.random() * 120, 
        -Math.random() * 150,
        -Math.random() * 180,
        -Math.random() * 200
      ],
      cumulative: [
        Math.random() * 50, 
        Math.random() * 60, 
        Math.random() * 70, 
        Math.random() * 80,
        Math.random() * 90,
        Math.random() * 100
      ]
    };
  } else if (url.includes('schedule_variance')) {
    // Enhanced schedule variance data
    return {
      labels: ['Q1 2022', 'Q2 2022', 'Q3 2022', 'Q4 2022', 'Q1 2023', 'Q2 2023'],
      positive_variance: [
        Math.random() * 30, 
        Math.random() * 30, 
        Math.random() * 35, 
        Math.random() * 40,
        Math.random() * 45,
        Math.random() * 50
      ],
      negative_variance: [
        -Math.random() * 30, 
        -Math.random() * 30, 
        -Math.random() * 35, 
        -Math.random() * 40,
        -Math.random() * 45,
        -Math.random() * 50
      ],
      milestones: [
        { name: 'Design Complete', date: '2022-02-15', status: 'Completed' },
        { name: 'Permits Approved', date: '2022-04-10', status: 'Completed' },
        { name: 'Foundation Complete', date: '2022-07-22', status: 'Completed' },
        { name: 'Structure Complete', date: '2022-12-05', status: 'Completed' },
        { name: 'MEP Rough-In', date: '2023-03-15', status: 'In Progress' },
        { name: 'Finishes', date: '2023-06-30', status: 'Planned' }
      ]
    };
  } else if (url.includes('fetch_hard_soft_costs_per_project')) {
    // Enhanced hard/soft costs data for comp search details with specific cost categories
    const mockData = {
      hard: {},
      soft: {}
    };
    
    // Hard cost categories
    const hardCostCategories = [
      'Site Work', 
      'Foundation', 
      'Structural Frame', 
      'Exterior Enclosure', 
      'Roofing', 
      'Interior Construction', 
      'MEP Systems', 
      'Finishes'
    ];
    
    // Soft cost categories
    const softCostCategories = [
      'Architecture & Engineering', 
      'Permits & Fees', 
      'Legal & Accounting', 
      'Insurance', 
      'Financing Costs', 
      'Project Management', 
      'Marketing', 
      'FF&E'
    ];
    
    // Generate data for 10 projects
    for (let i = 1; i <= 10; i++) {
      // Hard costs by category
      const hardCosts = {};
      let hardTotal = 0;
      hardCostCategories.forEach(category => {
        const value = Math.random() * 1000000 + 200000;
        hardCosts[category] = {
          original: value.toFixed(2),
          revised: (value * (1 + (Math.random() * 0.2 - 0.1))).toFixed(2),
          spent: (value * Math.random() * 0.9).toFixed(2)
        };
        hardTotal += parseFloat(hardCosts[category].revised);
      });
      
      // Soft costs by category
      const softCosts = {};
      let softTotal = 0;
      softCostCategories.forEach(category => {
        const value = Math.random() * 300000 + 50000;
        softCosts[category] = {
          original: value.toFixed(2),
          revised: (value * (1 + (Math.random() * 0.2 - 0.1))).toFixed(2),
          spent: (value * Math.random() * 0.9).toFixed(2)
        };
        softTotal += parseFloat(softCosts[category].revised);
      });
      
      mockData.hard[i] = {
        total_original: (hardTotal * 0.95).toFixed(2),
        total_revised: hardTotal.toFixed(2),
        categories: hardCosts
      };
      
      mockData.soft[i] = {
        total_original: (softTotal * 0.95).toFixed(2),
        total_revised: softTotal.toFixed(2),
        categories: softCosts
      };
    }
    
    return mockData;
  } else if (url.includes('leaves_sum_grouped_by_term')) {
    // Enhanced budget data with detailed breakdown for BudgetGrid.vue
    const categories = [
      {
        name: "ACQUISITION COSTS",
        subcategories: [
          "Land Purchase", 
          "Due Diligence", 
          "Closing Costs", 
          "Legal Fees"
        ]
      },
      {
        name: "HARD COSTS",
        subcategories: [
          "Site Work", 
          "Foundation", 
          "Structure", 
          "Exterior Envelope", 
          "Interior Finishes", 
          "MEP Systems", 
          "Elevators", 
          "General Conditions"
        ]
      },
      {
        name: "SOFT COSTS",
        subcategories: [
          "Architecture & Engineering", 
          "Permits & Fees", 
          "Legal & Accounting", 
          "Insurance", 
          "Testing & Inspection"
        ]
      },
      {
        name: "FINANCING COSTS",
        subcategories: [
          "Loan Fees", 
          "Interest Reserve", 
          "Construction Interest"
        ]
      },
      {
        name: "Owner Contingencies",
        subcategories: [
          "Design Contingency", 
          "Construction Contingency", 
          "Owner's Reserve"
        ]
      }
    ];
    
    // Generate detailed budget data with hierarchical structure
    let result = [];
    let id = 1;
    
    categories.forEach((category, catIndex) => {
      // Add main category
      const catBudget = Math.random() * 5000000 + 1000000;
      const catSpent = catBudget * Math.random() * 0.9;
      
      result.push({
        id: id++,
        dda_term_id: catIndex + 1,
        itemHierarchy: [category.name],
        original_budget: catBudget,
        current_budget: catBudget * (1 + (Math.random() * 0.1 - 0.05)),
        previously_funded: catSpent,
        current_request: catBudget * Math.random() * 0.1,
        total_completed_to_date_pct_avg: (catSpent / catBudget) * 100,
        hasChildren: true,
        isExpanded: true
      });
      
      // Add subcategories
      category.subcategories.forEach((subcat, subIndex) => {
        const subcatBudget = catBudget / category.subcategories.length * (0.8 + Math.random() * 0.4);
        const subcatSpent = subcatBudget * Math.random() * 0.9;
        
        result.push({
          id: id++,
          dda_term_id: catIndex + 1,
          itemHierarchy: [category.name, subcat],
          original_budget: subcatBudget,
          current_budget: subcatBudget * (1 + (Math.random() * 0.1 - 0.05)),
          previously_funded: subcatSpent,
          current_request: subcatBudget * Math.random() * 0.1,
          total_completed_to_date_pct_avg: (subcatSpent / subcatBudget) * 100,
          parentId: catIndex + 1
        });
      });
    });
    
    return result;
  } else if (url.includes('budget')) {
    // Enhanced general budget data
    const budgetCategories = [
      'Acquisition', 
      'Site Work', 
      'Foundation', 
      'Structure', 
      'Exterior', 
      'Interior', 
      'MEP', 
      'Finishes', 
      'Soft Costs', 
      'Financing',
      'Contingency'
    ];
    
    return budgetCategories.map((category, index) => {
      const originalBudget = Math.random() * 1000000 + 100000;
      const currentBudget = originalBudget * (1 + (Math.random() * 0.2 - 0.1));
      const previouslyFunded = currentBudget * Math.random() * 0.8;
      
      return {
        id: index + 1,
        name: category,
        original_budget: originalBudget,
        current_budget: currentBudget,
        previously_funded: previouslyFunded,
        current_request: currentBudget * Math.random() * 0.15,
        total_completed_to_date_pct_avg: (previouslyFunded / currentBudget) * 100,
        variance: currentBudget - originalBudget,
        variance_pct: ((currentBudget - originalBudget) / originalBudget) * 100
      };
    });
  } else if (url.includes('comp')) {
    // Generate detailed cost structure for each property
    const generateDetailedCosts = (propertyId, totalCost, size) => {
      const costPerSF = totalCost / size;
      
      // Hard costs breakdown (typically 70-80% of total costs)
      const hardCostTotal = totalCost * 0.75;
      const hardCosts = {
        total: hardCostTotal,
        cost_per_sf: hardCostTotal / size,
        breakdown: {
          site_work: {
            cost: hardCostTotal * 0.08,
            cost_per_sf: hardCostTotal * 0.08 / size,
            percent_of_total: 8
          },
          foundation: {
            cost: hardCostTotal * 0.12,
            cost_per_sf: hardCostTotal * 0.12 / size,
            percent_of_total: 12
          },
          structure: {
            cost: hardCostTotal * 0.20,
            cost_per_sf: hardCostTotal * 0.20 / size,
            percent_of_total: 20
          },
          exterior_enclosure: {
            cost: hardCostTotal * 0.15,
            cost_per_sf: hardCostTotal * 0.15 / size,
            percent_of_total: 15
          },
          roofing: {
            cost: hardCostTotal * 0.05,
            cost_per_sf: hardCostTotal * 0.05 / size,
            percent_of_total: 5
          },
          interior_construction: {
            cost: hardCostTotal * 0.15,
            cost_per_sf: hardCostTotal * 0.15 / size,
            percent_of_total: 15
          },
          mep_systems: {
            cost: hardCostTotal * 0.20,
            cost_per_sf: hardCostTotal * 0.20 / size,
            percent_of_total: 20
          },
          finishes: {
            cost: hardCostTotal * 0.05,
            cost_per_sf: hardCostTotal * 0.05 / size,
            percent_of_total: 5
          }
        }
      };
      
      // Soft costs breakdown (typically 20-30% of total costs)
      const softCostTotal = totalCost * 0.25;
      const softCosts = {
        total: softCostTotal,
        cost_per_sf: softCostTotal / size,
        breakdown: {
          architecture_engineering: {
            cost: softCostTotal * 0.30,
            cost_per_sf: softCostTotal * 0.30 / size,
            percent_of_total: 30
          },
          permits_fees: {
            cost: softCostTotal * 0.15,
            cost_per_sf: softCostTotal * 0.15 / size,
            percent_of_total: 15
          },
          legal_accounting: {
            cost: softCostTotal * 0.10,
            cost_per_sf: softCostTotal * 0.10 / size,
            percent_of_total: 10
          },
          insurance: {
            cost: softCostTotal * 0.08,
            cost_per_sf: softCostTotal * 0.08 / size,
            percent_of_total: 8
          },
          financing: {
            cost: softCostTotal * 0.25,
            cost_per_sf: softCostTotal * 0.25 / size,
            percent_of_total: 25
          },
          project_management: {
            cost: softCostTotal * 0.07,
            cost_per_sf: softCostTotal * 0.07 / size,
            percent_of_total: 7
          },
          testing_inspection: {
            cost: softCostTotal * 0.05,
            cost_per_sf: softCostTotal * 0.05 / size,
            percent_of_total: 5
          }
        }
      };
      
      // Project tracking data
      const startDate = new Date(2020 + Math.floor(Math.random() * 3), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28));
      const durationMonths = Math.floor(Math.random() * 24) + 12;
      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + durationMonths);
      
      const progress = Math.random() * 100;
      const delayInDays = Math.floor(Math.random() * 60) - 30; // Can be negative (ahead of schedule)
      
      const tracking = {
        schedule: {
          start_date: startDate.toISOString().split('T')[0],
          planned_end_date: endDate.toISOString().split('T')[0],
          actual_end_date: delayInDays > 0 ? 
            (() => {
              const actualEnd = new Date(endDate);
              actualEnd.setDate(actualEnd.getDate() + delayInDays);
              return actualEnd.toISOString().split('T')[0];
            })() : 
            null,
          duration_months: durationMonths,
          progress_percent: progress,
          delay_days: delayInDays,
          status: progress >= 100 ? 'Completed' : 
                  progress > 0 ? 'In Progress' : 'Not Started',
          milestones: [
            { 
              name: 'Design Complete', 
              planned_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 2);
                return date.toISOString().split('T')[0];
              })(),
              actual_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 2);
                date.setDate(date.getDate() + Math.floor(Math.random() * 30) - 15);
                return date.toISOString().split('T')[0];
              })(),
              status: 'Completed'
            },
            { 
              name: 'Permits Approved', 
              planned_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 4);
                return date.toISOString().split('T')[0];
              })(),
              actual_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 4);
                date.setDate(date.getDate() + Math.floor(Math.random() * 45));
                return date.toISOString().split('T')[0];
              })(),
              status: 'Completed'
            },
            { 
              name: 'Foundation Complete', 
              planned_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 6);
                return date.toISOString().split('T')[0];
              })(),
              actual_date: progress > 20 ? (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 6);
                date.setDate(date.getDate() + Math.floor(Math.random() * 30) - 10);
                return date.toISOString().split('T')[0];
              })() : null,
              status: progress > 20 ? 'Completed' : 'Planned'
            },
            { 
              name: 'Structure Complete', 
              planned_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 10);
                return date.toISOString().split('T')[0];
              })(),
              actual_date: progress > 50 ? (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 10);
                date.setDate(date.getDate() + Math.floor(Math.random() * 30) - 10);
                return date.toISOString().split('T')[0];
              })() : null,
              status: progress > 50 ? 'Completed' : 'Planned'
            },
            { 
              name: 'MEP Rough-In', 
              planned_date: (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 14);
                return date.toISOString().split('T')[0];
              })(),
              actual_date: progress > 70 ? (() => {
                const date = new Date(startDate);
                date.setMonth(date.getMonth() + 14);
                date.setDate(date.getDate() + Math.floor(Math.random() * 30) - 10);
                return date.toISOString().split('T')[0];
              })() : null,
              status: progress > 70 ? 'Completed' : progress > 60 ? 'In Progress' : 'Planned'
            }
          ]
        },
        budget: {
          total_budget: totalCost,
          spent_to_date: totalCost * (progress / 100),
          remaining: totalCost - (totalCost * (progress / 100)),
          percent_spent: progress,
          variance: totalCost * (Math.random() * 0.1 - 0.05), // -5% to +5% variance
          variance_percent: (Math.random() * 10 - 5).toFixed(2), // -5% to +5%
          forecast_at_completion: totalCost * (1 + (Math.random() * 0.1 - 0.05))
        }
      };
      
      return {
        hard_costs: hardCosts,
        soft_costs: softCosts,
        tracking: tracking
      };
    };
    
    // Properties with detailed cost data
    const detailedProperties = [
      { 
        id: 1, 
        name: 'One Bryant Park', 
        type: 'Office', 
        size: 2350000, 
        year_built: 2009, 
        address: '1 Bryant Park, New York, NY',
        cost_per_sf: 1270,
        total_cost: 2984500000,
        leed_rating: 'Platinum',
        floors: 51,
        height_ft: 945,
        construction_duration_months: 36
      },
      { 
        id: 2, 
        name: 'Hudson Yards Tower', 
        type: 'Office', 
        size: 1750000, 
        year_built: 2019, 
        address: '30 Hudson Yards, New York, NY',
        cost_per_sf: 1350,
        total_cost: 2362500000,
        leed_rating: 'Gold',
        floors: 73,
        height_ft: 1268,
        construction_duration_months: 42
      },
      { 
        id: 3, 
        name: 'SoHo Retail Complex', 
        type: 'Retail', 
        size: 125000, 
        year_built: 2016, 
        address: '500 Broadway, New York, NY',
        cost_per_sf: 980,
        total_cost: 122500000,
        leed_rating: 'Silver',
        floors: 5,
        height_ft: 85,
        construction_duration_months: 18
      },
      { 
        id: 4, 
        name: 'The Chelsea Residences', 
        type: 'Multifamily', 
        size: 350000, 
        year_built: 2020, 
        address: '505 W 23rd St, New York, NY',
        cost_per_sf: 850,
        total_cost: 297500000,
        leed_rating: 'Gold',
        floors: 22,
        height_ft: 275,
        units: 284,
        construction_duration_months: 28
      },
      { 
        id: 5, 
        name: 'Downtown Mixed-Use', 
        type: 'Mixed-Use', 
        size: 520000, 
        year_built: 2018, 
        address: '400 Lafayette St, New York, NY',
        cost_per_sf: 1100,
        total_cost: 572000000,
        leed_rating: 'Gold',
        floors: 32,
        height_ft: 388,
        units: 186,
        retail_sf: 75000,
        construction_duration_months: 32
      }
    ];
    
    // Add detailed cost data to each property
    const propertiesWithCosts = detailedProperties.map(property => {
      const costs = generateDetailedCosts(property.id, property.total_cost, property.size);
      return {
        ...property,
        ...costs
      };
    });
    
    // Analytics and charts for comparison
    const hcAnalytics = {
      averages: {
        site_work_percent: 8.5,
        foundation_percent: 11.8,
        structure_percent: 19.5,
        exterior_percent: 15.2,
        roofing_percent: 4.8,
        interior_percent: 14.7,
        mep_percent: 20.3,
        finishes_percent: 5.2
      },
      cost_per_sf: {
        min: 600,
        max: 950,
        average: 785,
        median: 810
      },
      by_building_type: {
        office: { average_cost_per_sf: 870, average_percent_of_total: 76 },
        retail: { average_cost_per_sf: 650, average_percent_of_total: 72 },
        multifamily: { average_cost_per_sf: 710, average_percent_of_total: 78 },
        mixed_use: { average_cost_per_sf: 780, average_percent_of_total: 75 }
      }
    };
    
    const scAnalytics = {
      averages: {
        arch_eng_percent: 29.5,
        permits_fees_percent: 14.8,
        legal_accounting_percent: 10.2,
        insurance_percent: 8.1,
        financing_percent: 24.7,
        pm_percent: 7.5,
        testing_percent: 5.2
      },
      cost_per_sf: {
        min: 180,
        max: 320,
        average: 245,
        median: 260
      },
      by_building_type: {
        office: { average_cost_per_sf: 280, average_percent_of_total: 24 },
        retail: { average_cost_per_sf: 230, average_percent_of_total: 28 },
        multifamily: { average_cost_per_sf: 210, average_percent_of_total: 22 },
        mixed_use: { average_cost_per_sf: 260, average_percent_of_total: 25 }
      }
    };
    
    // Return enhanced comp set data
    return {
      success: true,
      data: {
        comparisons: [
          { id: 1, name: 'Office Building Comparison', count: 5, created_at: '2023-02-15' },
          { id: 2, name: 'Retail Center Analysis', count: 3, created_at: '2023-03-10' },
          { id: 3, name: 'Multifamily Housing Study', count: 7, created_at: '2023-01-20' },
          { id: 4, name: 'Mixed-Use Development Benchmark', count: 4, created_at: '2023-04-05' },
          { id: 5, name: 'Hospitality Properties Review', count: 6, created_at: '2023-05-12' }
        ],
        properties: propertiesWithCosts,
        metrics: {
          cost_per_sf: {
            average: 1110,
            min: 850,
            max: 1350,
            median: 1100
          },
          construction_duration: {
            average: 31.2,
            min: 18,
            max: 42,
            median: 32
          },
          leed_distribution: {
            Platinum: 1,
            Gold: 3,
            Silver: 1,
            Certified: 0
          }
        },
        analytics: {
          hard_costs: hcAnalytics,
          soft_costs: scAnalytics
        },
        charts: {
          cost_breakdown: {
            labels: ['Hard Costs', 'Soft Costs'],
            data: [75, 25]
          },
          hard_cost_breakdown: {
            labels: ['Site Work', 'Foundation', 'Structure', 'Exterior', 'Roofing', 'Interior', 'MEP', 'Finishes'],
            data: [8, 12, 20, 15, 5, 15, 20, 5]
          },
          soft_cost_breakdown: {
            labels: ['Arch & Eng', 'Permits & Fees', 'Legal & Acct', 'Insurance', 'Financing', 'Project Mgmt', 'Testing'],
            data: [30, 15, 10, 8, 25, 7, 5]
          },
          cost_by_property: {
            labels: detailedProperties.map(p => p.name),
            hard_costs: detailedProperties.map(p => p.total_cost * 0.75),
            soft_costs: detailedProperties.map(p => p.total_cost * 0.25)
          }
        }
      }
    };
  } else {
    // For any other endpoint, return a basic array
    return Array(3).fill().map((_, index) => ({
      id: index + 1,
      name: `Item ${index + 1}`,
      value: Math.random() * 1000
    }));
  }
}

export function errorManagement(error, errorStore, errorTitleName) {
  // For development mode, just log the error and return
  if (process.env.NODE_ENV === 'development') {
    console.error('Error:', error);
    return Promise.reject(error);
  }
  
  // Check if errorStore is defined and has the setError method
  if (!errorStore || typeof errorStore.setError !== 'function') {
    console.error('Error store not available or setError method not found');
    return Promise.reject(error);
  }
  
  if (error && error.response && error.response.status === 401) {
    errorStore.setError('Login Expired', errorStore.getLoggedOutMessage)
  }
  else if (error && error.response && error.response.status === 403) {
    errorStore.setError("Error " + errorTitleName, 'You do not have access to this data.')
  } else if (error.response) {
    // The client was given an error response (5xx, 4xx)
    const message = formatErrorMessage(errorTitleName, error)
    errorStore.setError("Error " + errorTitleName, message)
  } else if (error.request) {
    // The client never received a response, and the request was never left
    errorStore.setError("Error " + errorTitleName, 'Error receiving a response from the server. Error message:' + error.message)
  } else {
    // Anything else
    errorStore.setError("Error " + errorTitleName, error.message)
  }
  return Promise.reject(error)
}

export function formatErrorMessage(tableName, error) {
  let { ...errorObject } = error
  let message = ''
  if (errorObject?.response && errorObject.response.status === 404) {
    return `Server Error: ${errorObject.response.statusText}`
  } else if (errorObject?.response && errorObject.response.status === 403) {
    return 'You do not have access to this data.'
  } else if ((error?.response || errorObject) && (errorObject.status === 400 || errorObject.code === 'ERR_BAD_REQUEST')) {
    // get the proper error object as it can vary
    if (errorObject.response) {
      errorObject = errorObject.response
    } else if (error?.response) {
      errorObject = error.response
    }
    if (errorObject.data && typeof (errorObject.data[tableName]) === 'string') {
      message += errorObject.data[tableName]
    } else if (errorObject.data && errorObject.data[tableName]) {
      errorObject.data[tableName].forEach(errors => {
        if (typeof (errors) === 'string') {
          message += errors + "; "
        } else {
          Object.keys(errors).forEach((key) => {
            let error_keys = typeof (errors[key]) === 'object' ? errors[key].join(',') : String(errors[key].join(','))
            message += "*" + key + "*: " + error_keys + "; "
          })
        }
      })
    } else if (errorObject.data && typeof (errorObject.data) === 'object') {
      Object.keys(errorObject.data).forEach((key) => {
        let error_keys = typeof (errorObject.data[key]) === 'object' && errorObject.data[key] ? errorObject.data[key].join(',') : String(errorObject.data[key])
        message += "*" + key + "*: " + error_keys + "; "
      })
    } else {
      message = 'validations failed'
    }
    return message
  } else if (errorObject) {
    message = errorObject.status ? (errorObject.status + '. ') : ''
    message = message + (errorObject.errorData ? (errorObject.errorData + '. ') : '')
    message = message + (errorObject.exception ? (errorObject.exception + '. ') : '')
    return message
  } else {
    return 'Unknown Error'
  }
}

export function largeEditWarning(count) {
  if (count > 10) {
    const answer = window.confirm(
      `WARNING: You have selected ${count} items to move. Are you sure you want to do this?`
    )
    // cancel the navigation and stay on the same page
    if (!answer) return false
  }

  return true
}

export function downloadPdf(downloadFileName, resultData) {
  let pdfFile = new Blob([resultData], {
    type: "application/pdf",
    'Content-Disposition': 'attachment'
  })
  let pdfUrl = URL.createObjectURL(pdfFile)

  let anchor = document.createElement('a')
  anchor.href = pdfUrl
  anchor.target = '_blank'
  // eslint-disable-next-line no-unused-vars
  anchor.download = downloadFileName || 'file.pdf'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

export function standardizeLabelLength(label, maxLength = 15) {
  if (label && label.length > maxLength - 3) {
    return label.slice(0, maxLength) + "...";
  }
  return label
}
export function standardizeLabelsLength(labels, maxLength = 15) {
  if (!labels) { return labels }

  let newLabels = []
  labels.forEach(label => {
    if (label && label.length > maxLength - 3) {
      newLabels.push(label.slice(0, maxLength) + "...")
    } else {
      newLabels.push(label)
    }
  })
  return newLabels
}
export function percentFormat(percent) {
  if (percent === undefined || percent === null) { return '' }
  let amount = Math.round(parseFloat(percent).toFixed(2) * 100)
  amount = formatDecimals(amount) // limit accuracy / detail
  return amount + '%'
}
export function numberFormat(number, numDecimals = 2) {
  if (number === undefined || number === null) { return '' }
  let parts = number.toString().split(".")
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  if (numDecimals !== null && parts[1]) {
    parts[1] = parts[1].slice(0, numDecimals)
  }
  return numDecimals !== 0 ? parts.join(".") : parts[0]
}
// get the tens digit after decimal
export function decimalPart(number) {
  if (number === undefined || number === null) { return '' }
  let parts = number.toString().split(".")
  if (parts[1]) {
    return parseFloat(`0.${parts[1].slice(0, 1)}`)
  }
  return 0.0
}
export function kMillionsFormat(money) {
  if (!money && money !== 0.0) {
    return ''
  } else if (Math.abs(money) >= 1000000000) {
    return "$" + (money / 100000000).toFixed(0) + "B";
  } else if (Math.abs(money) >= 1000000) {
    return "$" + (money / 1000000).toFixed(0) + "M";
  } else if (Math.abs(money) >= 1000) {
    return "$" + (money / 1000).toFixed(0) + "k";
  }

  return currencyFormat(money.toFixed(0))
}

export function kMillionsWithDecimalsFormat(money) {
  if (!money && money !== 0.0) {
    return ''
  } else if (Math.abs(money) >= 1000000000) {
    return "$" + (money / 1000000000).toFixed(1) + "B";
  } else if (Math.abs(money) >= 1000000) {
    return "$" + (money / 1000000).toFixed(1) + "M";
  } else if (Math.abs(money) >= 1000) {
    return "$" + (money / 1000).toFixed(1) + "k";
  }

  return currencyFormat(money)
}
export function currencyFormat(money, skipDecimal) {
  if (money === undefined || money === null || (!parseFloat(money) && money !== 0)) { return '' }
  let amount = parseFloat(money).toFixed(skipDecimal ? 0 : 2)
  // Add commas
  amount = amount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
  return '$' + amount
}
// new version of currency format with (0.25 / 0.75) decimal rounding applied
export function currencyFormatRoundedDecimal(money) {
  if (!isNaN(parseFloat(money))) {
    let amount = formatDecimals(money)
    // Add commas
    amount = amount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
    return '$' + amount
  }
  return null
}

// Remove currency formatting from money
export function removeCurrencyFormat(money) {
  if (money === undefined || money === null || money === '') { return money }
  if (!money.toString().match(/\$[0-9-,.]+/)) { return money }
  // Remove commas and $
  return parseFloat(money.toString().replace(/[$,]/g, ''));
}
// second parameter lets you choose whether or not time is shown
export function dateFormat(dateStr, includeTime) {
  if (!dateStr) { return '' }
  let formatted = null
  if (typeof (dateStr) !== 'string' || (dateStr.indexOf('T') > -1 || dateStr.indexOf('Z') > -1)) {
    formatted = new Date(dateStr)
  } else {
    // honor that this is coming as a date that needs to avoid time zone conversion
    formatted = new Date(dateStr + 'T12:00:00.000Z')
  }
  // Not sure why a comma is coming in here....
  formatted = formatted.toLocaleString().replace(',', '')
  return includeTime ? formatted : formatted.split(' ')[0]
}
// only show time, allow to choose whether show seconds
export function timeFormat(dateStr, includeSeconds, includeAmPm) {
  if (!dateStr) { return '' }
  let formatted = new Date(dateStr)
  // Not sure why a comma is coming in here....
  // Example output from formatted.toLocaleString(): 3/9/2023, 1:11:26 PM
  formatted = formatted.toLocaleString().replace(',', '')
  // get AM or PM and convert to lower case
  let ampm = formatted.split(' ')[2]?.toLowerCase()
  // Get h:mm:ss
  formatted = formatted.split(' ')[1].split(':')
  formatted = includeSeconds ? formatted.join(':') : formatted[0] + ':' + formatted[1]
  return includeAmPm ? formatted + ampm : formatted
}
// Convert a Javascript date to a SQL-friendly date
export function jsDateToSqlDate(date, addTime) {
  if (!date) { return '' }
  if (addTime) {
    throw new Error('This can introduce hard to solve bugs where dates keep getting incremented by a day!')
    // const now = new Date()
    // date = new Date(date.getUTCFullYear(), date.getUTCMonth(),
    //     date.getUTCDate(), now.getUTCHours(),
    //     now.getUTCMinutes(), now.getUTCSeconds())
    // return date.getUTCFullYear() + '-' + prependZero(date.getUTCMonth() + 1) + '-' + prependZero(date.getUTCDate()) + ' T' + prependZero(now.getUTCHours()) + ':' + prependZero(now.getUTCMinutes()) + ':' + prependZero(now.getUTCSeconds()) + 'Z'
  } else {
    return date.getUTCFullYear() + '-' + prependZero(date.getUTCMonth() + 1) + '-' + prependZero(date.getUTCDate()) + ' T' + prependZero(date.getUTCHours()) + ':' + prependZero(date.getUTCMinutes()) + ':' + prependZero(date.getUTCSeconds()) + 'Z'
  }
}
export function yyyymmddDateFormat(date) {
  if (!date) { return '' }
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()

  return `${year}${month}${day}`
}

function prependZero(num) {
  if (num < 10) {
    return `0${num}`
  }
  return num
}

// An extra protection before sending it to the backend.
// This isn't perfect as the backend is what does the heavy lifting.
// A lot of valid characters can be used by the client.
export function sanitizeString(text) {
  if (!text) { return text }
  return String(text).replace('`', '')
    .replace('@', '')
    .replace('""', '')
    .replace(`"'`, '')
    .replace(`'"`, '')
    .replace(/DROP TABLE/i, '')
    .replace(/DELETE FROM/i, '')
    .replace(/SElECT FROM/i, '');
}

export function strToHtml(str) {
  return str.replace(/[&<>'"]/g,
    tag => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[tag]));
}

export function formatDecimals(number, decimalPlace = 1) {
  if (!number && number !== 0) { return number }
  number = parseFloat(number)
  // Get first the whole number
  let decimal = number - Math.floor(number);
  // Checks the decimal numbers
  if (decimal < 0.25) {
    return Math.floor(number).toFixed(decimalPlace);
  } else if (decimal < 0.75) {
    return (Math.floor(number) + 0.5).toFixed(decimalPlace);
  } else {
    return Math.ceil(number).toFixed(decimalPlace);
  }
}
// Finds if num is in the range of num1 and num2
// From https://stackoverflow.com/a/71677332/2611078
export function inRange(num, num1, num2) {
   return Math.min(num1, num2) <= num && Math.max(num1, num2) >= num
}

// Find the median of an array of values
export function median(values) {
  if (values.length === 0) return 0;

  values.sort(function(a, b) {
    return a - b;
  });

  const half = Math.floor(values.length / 2);

  if (values.length % 2) {
    return values[half];
  } else {
    return (values[half - 1] + values[half]) / 2.0;
  }
}

// For Property Images 
export function getImageUrl(project) {
  if (!project || !project.image_url) {
    return '/img/property1.jpg';
  }
  
  // If the image_url starts with /img/ or /images/, it's a static path
  if (project.image_url.startsWith('/img/') || project.image_url.startsWith('/images/')) {
    return project.image_url;
  }
  
  // Otherwise, it's a URL from the API
  return project.image_url;
}

export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}
